<script setup lang="ts">
import { computed, ref } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import type { ApplicantEntry } from '@/types/Applicant';
import type { CompanyDetails } from '@/types';
import CompanyForm from './CompanyForm.vue';
import PersonCard from './PersonCard.vue';
import { createApplicant } from '@/helpers/builder';
import { ApplicantType } from '@/helpers/const';

const props = withDefaults(
  defineProps<{
    allIndividuals?: ApplicantEntry[];
    initialCompanyDetails?: CompanyDetails;
    initialDirectors?: ApplicantEntry[];
    isPrimaryBorrower?: boolean;
    forceShowErrors?: boolean;
  }>(),
  {
    allIndividuals: () => [],
    initialCompanyDetails: () => ({}),
    initialDirectors: () => [],
    isPrimaryBorrower: false,
    forceShowErrors: false,
  },
);

const emit = defineEmits<{
  (e: 'relationship-change', individualId: string, newStatus: string, oldStatus?: string): void;
  (e: 'sync-applicant', applicant: ApplicantEntry): void;
  (e: 'remove-applicant', applicantId: string): void;
}>();

const validationError = ref('');

const companyDetails = ref<CompanyDetails>({ ...props.initialCompanyDetails });
const directors = ref<ApplicantEntry[]>(
  props.initialDirectors.map((d, index) => ({
    ...d,
    type: props.isPrimaryBorrower && index === 0 ? ApplicantType.PRIMARY : d.type,
  })),
);
const directorRefs = useTemplateRefsList<InstanceType<typeof PersonCard>>();

const showCompanyForm = ref(!props.initialCompanyDetails?.companyName);
const companyFormRef = ref<InstanceType<typeof CompanyForm>>();

const hasAnySavedDirector = computed(() => directors.value.some((d) => d.firstName && d.lastName));
const isCompanySaved = computed(() => !showCompanyForm.value && Boolean(companyDetails.value.companyName));
const displayName = computed(() => companyDetails.value.companyName || '');
const excludedDirectorIds = computed(() => directors.value.filter((d) => d.firstName && d.lastName).map((d) => d.id));

const addDirector = () => {
  const isFirstDirector = directors.value.length === 0;
  const type = props.isPrimaryBorrower && isFirstDirector ? ApplicantType.PRIMARY : ApplicantType.SECONDARY;
  const newDirector = createApplicant(type);
  directors.value = [...directors.value, newDirector];
};

const removeDirector = (directorId: string) => {
  const director = directors.value.find((d) => d.id === directorId);
  const wasSaved = director?.firstName && director?.lastName;
  directors.value = directors.value.filter((d) => d.id !== directorId);
  if (directors.value.length === 0) {
    showCompanyForm.value = true;
  }
  if (wasSaved) {
    emit('remove-applicant', directorId);
  }
};

const saveCompanyAndAddFirstDirector = async () => {
  const companyValid = (await companyFormRef.value?.update()) ?? false;
  if (companyValid) {
    showCompanyForm.value = false;
    if (directors.value.length === 0) {
      addDirector();
    }
  }
  return companyValid;
};

const editCompanyDetails = () => {
  showCompanyForm.value = true;
};

const cancelCompanyEdit = () => {
  companyFormRef.value?.cancel();
  if (companyDetails.value.companyName && hasAnySavedDirector.value) {
    showCompanyForm.value = false;
  }
};

const update = async () => {
  validationError.value = '';

  const isCompanyFormVisible = showCompanyForm.value || directors.value.length === 0;
  if (isCompanyFormVisible) {
    const companyValid = (await companyFormRef.value?.update()) ?? false;
    if (!companyValid) {
      return false;
    }
  }

  const hasCompanyName = Boolean(companyDetails.value.companyName);
  const hasSavedDirector = directors.value.some((d) => d.firstName && d.lastName);

  if (hasCompanyName && !hasSavedDirector) {
    validationError.value = 'At least one director needs to be nominated';
  }

  if (directors.value.length > 0) {
    const directorValidations = await Promise.all(
      directorRefs.value.filter((ref) => ref?.update).map((ref) => ref.update()),
    );
    if (!directorValidations.every(Boolean)) {
      return false;
    }
  }

  return hasCompanyName && hasSavedDirector;
};

const cancel = () => {
  companyFormRef.value?.cancel();
};

const expandDirector = (directorId: string): boolean => {
  const index = directors.value.findIndex((d) => d.id === directorId);
  if (index >= 0 && directorRefs.value[index]) {
    directorRefs.value[index].expandAndFocus();
    return true;
  }
  return false;
};

defineExpose({
  update,
  cancel,
  displayName,
  isCompanySaved,
  editCompanyDetails,
  companyDetails,
  directors,
  expandDirector,
});
</script>

<template>
  <div class="company-section">
    <div v-if="showCompanyForm || directors.length === 0">
      <CompanyForm ref="companyFormRef" v-model="companyDetails" data-test-id="company-form" />
      <div class="mt-8 flex-shrink-0">
        <BxButton
          id="company-save"
          data-test-id="company-save"
          variant="secondary"
          @click="saveCompanyAndAddFirstDirector()"
        >
          Save
        </BxButton>
        <BxButton
          v-if="companyDetails.companyName"
          data-test-id="company-cancel"
          variant="link"
          class="ml-4"
          @click="cancelCompanyEdit()"
        >
          Cancel
        </BxButton>
      </div>
    </div>

    <div v-if="directors.length > 0 && !showCompanyForm" class="directors-section space-y-4">
      <PersonCard
        v-for="(director, index) in directors"
        :key="director.id"
        :ref="directorRefs.set"
        v-model="directors[index]"
        :data-test-id="`personcard-director-${index}`"
        :index="index"
        role="director"
        :all-individuals="[...props.allIndividuals, ...directors]"
        :current-id="director.id"
        :force-show-errors="props.forceShowErrors"
        :excluded-applicant-ids="excludedDirectorIds"
        @remove="removeDirector"
        @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
        @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
      />
      <div v-if="hasAnySavedDirector" class="pt-2">
        <BxButton
          id="add-another-director"
          data-test-id="company-add-director"
          variant="tertiary"
          @click="addDirector()"
        >
          + Add director
        </BxButton>
      </div>
    </div>

    <BxErrorPanel v-if="validationError" class="mt-4">
      {{ validationError }}
    </BxErrorPanel>
  </div>
</template>
